
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { PublicHeader } from '@/components/PublicHeader';
import { AuthRedirectHandler } from '@/components/auth/AuthRedirectHandler';
import { LoginForm } from '@/components/auth/LoginForm';
import { InvitationSignUpForm } from '@/components/auth/InvitationSignUpForm';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useAuthHandlers } from '@/hooks/useAuthHandlers';
import { useInvitationHandler } from '@/hooks/useInvitationHandler';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitation');
  const [activeTab, setActiveTab] = useState(invitationId ? 'signup' : 'login');
  const {
    isLoading,
    error,
    success,
    handleLogin,
    handleSignUp,
    handleResetPassword,
    handleResendConfirmation,
    clearMessages
  } = useAuthHandlers();

  const {
    invitation,
    loading: invitationLoading,
    error: invitationError,
    processInvitationSignup
  } = useInvitationHandler(invitationId);

  useEffect(() => {
    if (invitationId) {
      setActiveTab('signup');
    }
  }, [invitationId]);

  const resetForm = () => {
    clearMessages();
  };

  const handleInvitationSignUp = async (email: string, password: string, firstName: string, lastName: string) => {
    return await processInvitationSignup(email, password, firstName, lastName);
  };

  // For invitation links, always show the invitation content
  if (invitationId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-64px)]">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Accept Invitation</CardTitle>
                <CardDescription>
                  {invitationLoading && 'Loading invitation details...'}
                  {invitation && `You've been invited to join CampChef Pro${invitation.organizationName ? ` for ${invitation.organizationName}` : ''}`}
                  {invitationError && 'Error loading invitation'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {invitationLoading && (
                    <div className="text-center py-8">
                      <LoadingSpinner message="Loading invitation details..." size="md" />
                    </div>
                  )}

                  {invitationError && (
                    <Alert variant="destructive">
                      <AlertDescription>{invitationError}</AlertDescription>
                    </Alert>
                  )}

                  {invitation && !invitationLoading && (
                    <InvitationSignUpForm
                      invitation={invitation}
                      onSubmit={handleInvitationSignUp}
                      isLoading={isLoading}
                    />
                  )}

                  {error && (
                    <Alert className="mt-4" variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="mt-4">
                      <AlertDescription>{success}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // For non-invitation flows, use AuthRedirectHandler for authenticated users
  return (
    <AuthRedirectHandler>
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-64px)]">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Welcome Back</CardTitle>
                <CardDescription>
                  Sign in to your CampChef Pro account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <LoginForm
                    onSubmit={handleLogin}
                    onResetPassword={handleResetPassword}
                    onResendConfirmation={handleResendConfirmation}
                    isLoading={isLoading}
                  />
                  
                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      Don't have an account?{' '}
                      <Link to="/contact" className="text-primary hover:underline font-medium">
                        Contact us for access
                      </Link>
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert className="mt-4" variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="mt-4">
                    <AlertDescription>{success}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AuthRedirectHandler>
  );
};

export default Auth;
